<script>
    import Route, {router} from "../lib/Router/Router.svelte";
    import R, {LOADER} from "../lib/requests";
    import {USER_ROLE, USER_ROLES} from "../lib/constants";
    import user from "../stores/user";
    import Alert from "../stores/alerts";
    import Notifier from "../stores/notifier";
    import {formatDate, copyObjectProperties, sort} from "../lib/lib";
    import PageHeader from "../components/PageHeader.svelte";
    import TableHeader from "../components/TableHeader.svelte";
    import AppIcon from "../components/icons/AppIcon.svelte";
    import UserEditModal from "./UserEditModal.svelte";


    let users = null;
    let userId = 0;
    let editUserId = null;
    let search = {
        role: '',
        term: '',
    }
    let userRoles = [];

    const notificationTypes = [
        '',
        'SMS',
        'WhatsApp'
    ];

    const pageHeaderControls = [
        {id: 'create', name: 'CREATE USER', class: 'hidden-xs'},
        {id: 'create', name: '', icon: 'plus-circle', class: 'hidden-xl hidden-lg hidden-md hidden-sm'},
        {id: 'reload', name: '', icon: 'refresh'}
    ];

    const tableColumns = [
        {key: 'name', title: 'NAME', sortable: true, sort_way: 'ASC'},
        {key: 'email', title: 'EMAIL', sortable: true, class: 'hidden-xs'},
        {key: 'mobile', title: 'Notification', sortable: false, class: 'hidden-xs'},
        {key: 'login_at', title: 'LOGIN', sortable: true, class: 'hidden-xs'},
        {key: '', title: ''},
    ];

    const load = () => {
        const onSuccess = result => {
            users = result;
            users.map(item => {
                item.login_at = formatDate(item.login_at, 'y-m-d');
            });
        }

        const onFailure = err => {
        };

        R.handle(R.user.index, search, onSuccess, onFailure, LOADER.PAGE);
    }

    const onSearchChange = () => {
        load();
    }

    const onColumnSort = ({detail}) => {
        users = sort(users, detail.key, detail.sort_way === 'DESC');
    }

    const onCreate = () => {
        editUserId = 0;
    }

    const onEdit = (user) => {
        userId = user.id;
        editUserId = user.id;
    }

    const closeEdit = (user) => {
        if (user) {
            let properties = ['id', 'role', 'name', 'email', 'mobile', 'notification_type', 'notification_enabled', 'is_active', 'login_at'];
            let item = users.find(x => x.id == user.id);
            if (item) {
                item = copyObjectProperties(user, item, properties);
                users = users;
            } else {
                users = [copyObjectProperties(user, properties), ...users];
            }
        }
        editUserId = null;
    }

    const onDelete = (user) => {
        Alert.delete("Delete user", `This will permanently delete user "${user.name}".\nAre you sure?`, () => deleteUser(user.id));
    }

    const deleteUser = id => {
        const onSuccess = result => {
            users = users.filter(x => x.id != result);
            Notifier.showMessage("User deleted");
        }

        const onFailure = err => {
            if (err.code === 'not_allowed') {
                Alert.warning("Delete user", `You are not allowed to delete this user!`);
                return;
            }

            if (err.code === 'object_related') {
                let message = [];
                Object.keys(err.params).map(key => {
                    if (err.params[key] > 0) {
                        message.push(`${err.params[key]} ${key}`);
                    }
                });
                Alert.warning("Delete user", `User is related to ${message.join(", ")}.\nYou can not delete this user.`);
            }
        };

        R.handle(R.user.delete, id, onSuccess, onFailure, LOADER.POPUP);
    }


    // Set user roles in search selection
    Object.keys(USER_ROLES).map(key => {
        userRoles.push({id: key, name: USER_ROLES[key]});
    });
    if ($user.role == USER_ROLE.ADMIN) {
        userRoles = userRoles.filter(x => x.id != USER_ROLE.GOD);
    }

    load();
</script>

<PageHeader title="Users" controls={ pageHeaderControls } on:create={ onCreate } on:reload={ load }/>

<div class="panel-filters mb-5">
    <div class="filter-row">
        <div class="filter-col user-role border-after border-bottom-dotted-xs">
            <select class="form-select minimal-light px-2" bind:value="{ search.role }" on:change={ onSearchChange }>
                <option value="">All users</option>
                {#each userRoles as item}
                    <option value="{ item.id }">{ item.name }</option>
                {/each}
            </select>
        </div>
        <div class="filter-col-group full-width">
            <div class="filter-col user-name d-flex pe-0">
                <AppIcon icon="search" size="18" cssClass="icon-gray me-2"/>
                <input type="text" class="form-control minimal-light px-2" bind:value="{ search.term }"
                       placeholder="name or email"
                       on:change={ onSearchChange }>
            </div>
            <div class="filter-col">
                <button class="btn btn-outline" on:click="{ onSearchChange }">SEARCH</button>
            </div>
        </div>
    </div>
</div>

{#if users}
    <div class="panel">
        <div class="panel-body">
            {#if users.length}
                <table class="table table-list table-list-border">
                    <TableHeader {tableColumns} on:sort={ onColumnSort }/>
                    <tbody>
                    {#each users as item}
                        <tr class="row-hover" class:row-selected="{ userId === item.id }">
                            <td>
                                <div class="small">{ USER_ROLES[item.role] }</div>
                                <span class="is-link" class:not-active="{ item.is_active < 1 }"
                                      on:click="{ () => onEdit(item) }">
                                    { item.name }
                                </span>
                                {#if item.email}
                                    <div class="hidden-xl hidden-lg hidden-md hidden-sm text-gray">
                                        <a href="mailto:{ item.email }">{ item.email }</a>
                                    </div>
                                {/if}
                            </td>
                            <td class="text-gray hidden-xs">
                                { item.email }
                            </td>
                            <td class="text-gray hidden-xs">
                                {#if item.notification_type > 0}
                                    <div class="small">{notificationTypes[item.notification_type]}</div>
                                    <span class:not-active={item.notification_enabled == 0}>{ item.mobile }</span>
                                {/if}
                            </td>
                            <td class="text-gray date-column hidden-xs">
                                { formatDate(item.login_at, 'd-m-y') }
                            </td>
                            <td class="button-column pointer" title="delete user" on:click="{ () => onDelete(item) }">
                                <AppIcon icon="trash" size="16"/>
                            </td>
                        </tr>
                    {/each}
                    </tbody>
                </table>
            {/if}
        </div>
    </div>
{/if}
{#if editUserId != null}
    <UserEditModal userId="{ editUserId }" closeCallback="{ closeEdit }"/>
{/if}